import { Box } from '@chakra-ui/react';
import { memo, useCallback, useMemo } from 'react';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { sanitizeHtml, isValidEmbedCode } from '../../utils/sanitizeHtml';
import classNames from 'classnames';

// Move regex patterns outside component to prevent recreation
const YOUTUBE_REGEX = /(?:youtube\.com\/.*(?:\?|&)v=|youtu\.be\/)([^&]+)/;
const VIMEO_REGEX = /vimeo\.com\/(\d+)/;
const VIDEO_URL_REGEX = /(?:youtube\.com|youtu\.be|vimeo\.com)/;

const MediaDisplay = memo(({ media = [], rounded = false }) => {
  // Memoized URL checking functions
  const isVideoUrl = useCallback((url) => VIDEO_URL_REGEX.test(url), []);

  const extractYoutubeVideoId = useCallback((url) => {
    const match = url.match(YOUTUBE_REGEX);
    return match ? match[1] : null;
  }, []);

  const extractVimeoVideoId = useCallback((url) => {
    const match = url.match(VIMEO_REGEX);
    return match ? match[1] : null;
  }, []);

  // Memoized media renderer
  const renderMediaItem = useCallback(
    (item, key) => {
      if (isValidEmbedCode(item)) {
        const sanitized = sanitizeHtml(item);

        return (
          <LazyLoadComponent>
            <div
              className="w-full h-[200px] md:h-[300px]"
              dangerouslySetInnerHTML={{ __html: sanitized }}
            />
          </LazyLoadComponent>
        );
      }

      if (isVideoUrl(item)) {
        if (item.includes('youtube') || item.includes('youtu.be')) {
          const videoId = extractYoutubeVideoId(item);
          if (videoId) {
            return (
              <LazyLoadComponent>
                <iframe
                  key={key || item}
                  className={`w-full h-[200px] md:h-[300px] border-0 ${rounded ? 'rounded-md' : ''}`}
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title="YouTube video player"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </LazyLoadComponent>
            );
          }
        } else if (item.includes('vimeo')) {
          const videoId = extractVimeoVideoId(item);
          if (videoId) {
            return (
              <LazyLoadComponent>
                <iframe
                  key={key || item}
                  className={`w-full  h-[200px] md:h-[300px] border-0 ${rounded ? 'rounded-md' : ''}`}
                  src={`https://player.vimeo.com/video/${videoId}`}
                  title="Vimeo video player"
                  loading="lazy"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </LazyLoadComponent>
            );
          }
        }
      }

      return (
        <LazyLoadComponent>
          <img
            key={key || item}
            className={classNames(
              'w-full',
              rounded ? 'rounded-md' : '',
              media.length > 1
                ? 'object-cover h-[200px] md:h-[300px]'
                : 'object-contain',
            )}
            src={item}
            alt="media"
            loading="lazy"
          />
        </LazyLoadComponent>
      );
    },

    [isVideoUrl, extractYoutubeVideoId, extractVimeoVideoId],
  );

  // Memoize the layout logic
  const mediaLayout = useMemo(() => {
    switch (media.length) {
      case 0:
        return null;
      case 1:
        return (
          <Box className="mb-2">
            {media.map((item, index) => renderMediaItem(item, index))}
          </Box>
        );
      case 2:
        return (
          <Box className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
            {media.map((item, index) => renderMediaItem(item, index))}
          </Box>
        );
      case 3:
        return (
          <Box className="flex flex-col gap-1 md:gap-2">
            <Box className="grid grid-cols-2 gap-1 md:gap-2">
              {[media[0], media[1]].map((item, index) =>
                renderMediaItem(item, index + 1),
              )}
            </Box>
            <Box className="w-full mb-2">{renderMediaItem(media[2], 2)}</Box>
          </Box>
        );
      default:
        return (
          <Box className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
            <Box className="flex flex-col gap-2">
              {[media[0], media[1]].map((item, index) =>
                renderMediaItem(item, index),
              )}
            </Box>
            <Box className="flex flex-col gap-2">
              {[media[2], media[3]].map((item, index) =>
                renderMediaItem(item, index + 2),
              )}
            </Box>
          </Box>
        );
    }
  }, [media, renderMediaItem]);

  return <>{mediaLayout}</>;
});

MediaDisplay.displayName = 'MediaDisplay';

export default MediaDisplay;
